import React from 'react'
import PropTypes from 'prop-types'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from '@/components/ui/dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import {useModalContext} from './modalContext'
import {Content} from '@builder.io/sdk-react'
import {cn} from '@/lib/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {customComponents} from 'hec/builder'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import modelMap from '@/builder/model-map'

export const ModalContent = ({modalContent, attributes}) => {
    const {modalOpen, setModalOpen} = useModalContext()
    const config = getConfig()
    const {className, ...restAttributes} = attributes

    const shouldQueryModalData = !modalContent?.value

    const {
        data: modalData,
        isLoading: isModalDataLoading,
        error: modalDataError
    } = useFetchBuilderReference({
        model: modelMap.modalContent,
        shouldQuery: shouldQueryModalData,
        referenceId: modalContent?.id
    })

    if (isModalDataLoading && shouldQueryModalData) {
        return <></>
    }

    if (modalDataError) {
        console.error(modalDataError)
        return <div>There was an error fetching Modal data.</div>
    }

    const modalContentData = modalContent?.value || modalData
    const modalName = modalContentData?.name
    const modalDescription = modalContentData?.data?.modalDescription || ''

    return (
        <Dialog
            open={modalOpen}
            onOpenChange={() => setModalOpen(!modalOpen)}
            data-testid="modalContent-Dialog"
        >
            <DialogContent
                className={cn('max-w-none overflow-y-auto', className)}
                onOpenAutoFocus={(e) => e.preventDefault()}
                {...restAttributes}
                data-testid="modalContent-DialogContent"
            >
                <VisuallyHidden.Root>
                    <DialogTitle data-testid="modalContent-DialogTitle">{modalName}</DialogTitle>
                    <DialogDescription data-testid="modalContent-DialogDescription">
                        {modalDescription}
                    </DialogDescription>
                </VisuallyHidden.Root>
                <DialogHeader data-testid="modalContent-DialogHeader">
                    {modalContentData && (
                        <Content
                            model={modelMap.modalContent}
                            content={modalContentData}
                            apiKey={config.app.builder.api}
                            customComponents={customComponents}
                            data-testid="modalContent"
                        />
                    )}
                </DialogHeader>
            </DialogContent>
        </Dialog>
    )
}

ModalContent.propTypes = {
    modalContent: PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.object
    }),
    attributes: PropTypes.shape({
        className: PropTypes.string
    })
}

export const ModalContentDefinition = {
    component: ModalContent,
    name: 'ModalContent',
    noWrap: true,
    canHaveChildren: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    hideFromInsertMenu: true,
    inputs: [
        {
            name: 'modalContent',
            type: 'reference',
            model: modelMap.modalContent,
            required: true
        }
    ]
}
